"use client"

import { Plus, Trash2, UserPlus } from "lucide-react"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { createEmptyGroupMember } from "@/store/appointment-store"
import { cn } from "@/lib/utils"
import { guestLabel } from "./GroupGuestTabs"
import { useStore } from "@/app/components/common/store.provider"

export const StepGuests = observer(function StepGuests() {
  const { appointmentStore, authStore } = useStore()
  const members = appointmentStore.groupMembers
  const maxSize = appointmentStore.maxGroupBookingSize
  const activeId = appointmentStore.activeMember?.localId
  const canAdd = members.length < maxSize
  const hasOrganizerSeat = members.some((m) => m.isOrganizerSeat)

  const addMyself = () => {
    const firstName = authStore.user?.profile?.firstName?.trim()
    appointmentStore.addGroupMember(
      createEmptyGroupMember({
        beneficiaryName: firstName ? `${firstName} (me)` : "Me",
        beneficiaryPhone: authStore.user?.profile?.phone ?? "",
        beneficiaryEmail: authStore.user?.email ?? "",
        isOrganizerSeat: true,
      }),
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Who is coming?</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Add everyone in your party. You will pick services and a time for each of
          them next.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">
          {members.length} of {maxSize} guests
        </p>
        <div className="flex flex-wrap gap-2">
          {!hasOrganizerSeat && canAdd ? (
            <Button type="button" variant="outline" size="sm" onClick={addMyself}>
              <UserPlus className="mr-1 h-4 w-4" />
              Add myself
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canAdd}
            onClick={() => appointmentStore.addGroupMember()}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add guest
          </Button>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No guests yet. Add yourself or someone else to get started.
          </p>
          <Button type="button" variant="outline" className="mt-4" onClick={addMyself}>
            <UserPlus className="mr-1 h-4 w-4" />
            Add myself
          </Button>
        </div>
      ) : null}

      <div className="space-y-4">
        {members.map((member, index) => {
          const isActive = member.localId === activeId
          return (
            <div
              key={member.localId}
              onFocusCapture={() => appointmentStore.setActiveGroupMember(member.localId)}
              className={cn(
                "space-y-4 rounded-lg border p-4 transition-colors",
                isActive ? "border-primary" : "border-border",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-foreground">
                  {guestLabel(member, index)}
                  {member.isOrganizerSeat ? (
                    <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                      Organizer
                    </span>
                  ) : null}
                </h2>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={`Remove ${guestLabel(member, index)}`}
                  onClick={() => appointmentStore.removeGroupMember(member.localId)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`name-${member.localId}`}>Full name</Label>
                  <Input
                    id={`name-${member.localId}`}
                    value={member.beneficiaryName}
                    onChange={(event) =>
                      appointmentStore.updateGroupMember(member.localId, {
                        beneficiaryName: event.target.value,
                      })
                    }
                    placeholder="e.g. Amaka Obi"
                    className="min-h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`phone-${member.localId}`}>Phone (optional)</Label>
                  <Input
                    id={`phone-${member.localId}`}
                    value={member.beneficiaryPhone}
                    onChange={(event) =>
                      appointmentStore.updateGroupMember(member.localId, {
                        beneficiaryPhone: event.target.value,
                      })
                    }
                    placeholder="+234…"
                    className="min-h-11"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor={`email-${member.localId}`}>Email (optional)</Label>
                  <Input
                    id={`email-${member.localId}`}
                    type="email"
                    value={member.beneficiaryEmail}
                    onChange={(event) =>
                      appointmentStore.updateGroupMember(member.localId, {
                        beneficiaryEmail: event.target.value,
                      })
                    }
                    placeholder="Sends them their own confirmation"
                    className="min-h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    Providing an email registers this guest as a customer so they can track their booking and hair history.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`notes-${member.localId}`}>Notes for the stylist</Label>
                <Textarea
                  id={`notes-${member.localId}`}
                  rows={2}
                  value={member.beneficiaryNotes}
                  onChange={(event) =>
                    appointmentStore.updateGroupMember(member.localId, {
                      beneficiaryNotes: event.target.value,
                    })
                  }
                  placeholder="Hair type, preferences, allergies…"
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
})
